import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { LearnArticleLink, LearnSeriesNav } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.controlar-numeros-restaurante-no-delegar.${field}`

const SOURCES = [
    {
        href: 'https://www.autonomosyemprendedor.es/articulo/autonomos/solo-cuatro-cada-diez-restaurantes-revisan-contabilidad-semanal-comprobar-son-rentables/20260318140307052630.html',
        labelKey: 'sources.1',
        fallback:
            'Diario AyE (Autónomos y Emprendedor), Solo cuatro de cada diez restaurantes revisan su contabilidad semanal para comprobar si son rentables, 18 de marzo de 2026',
    },
    {
        href: 'https://www.elespanol.com/aragon/cultura/20260323/ferran-adria-chef-michelin-restaurante-negocio-dificil-no-duran-dias/1003744179928_0.html',
        labelKey: 'sources.2',
        fallback:
            "El Español, Ferran Adrià, chef Michelin: 'Un restaurante es el negocio más difícil que hay', 23 de marzo de 2026",
    },
    {
        href: 'https://www.elespanol.com/cocinillas/actualidad-gastronomica/20251120/ferran-adria-restaurantes-no-duran-anos/1003744020677_0.html',
        labelKey: 'sources.3',
        fallback:
            "El Español (Cocinillas), Ferran Adrià: 'Seis de cada diez restaurantes no duran más de cinco años', 20 de noviembre de 2025",
    },
    {
        href: 'https://okdiario.com/gastronomia/ferran-adria-explica-que-cierran-tantos-restaurantes-espana-veredicto-no-deja-lugar-dudas-14911809',
        labelKey: 'sources.4',
        fallback: 'OKDiario, Ferran Adrià explica por qué cierran tantos restaurantes en España, 29 de junio de 2025',
    },
] as const

export function ControlarNumerosArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'quien', label: t(k('toc.1'), '1. ¿Quién tiene que controlar los números de un restaurante?') },
        {
            id: 'detalle',
            label: t(k('toc.2'), '2. El 70% se siente estable, pero solo el 39% revisa cada semana'),
        },
        { id: 'incomoda', label: t(k('toc.3'), '3. La parte incómoda: nadie lo va a hacer por ti') },
        { id: 'ganas', label: t(k('toc.4'), '4. ¿Qué ganas si empiezas a controlar tú los números?') },
        { id: 'cuenta-vieja', label: t(k('toc.5'), '5. 183.000 negocios sin termómetro semanal') },
        { id: 'ejemplo', label: t(k('toc.6'), '6. Un ejemplo con números') },
        { id: 'esta-semana', label: t(k('toc.7'), '7. Cinco acciones para retomar el control esta semana') },
        { id: 'matices', label: t(k('toc.8'), '8. Qué hay que tomar con pinzas en estos datos') },
    ]

    const weekItems = [
        t(
            k('week.1'),
            'Bloquea 15 minutos cada lunes para mirar tú, no tu gestor, el food cost y el ticket medio de la semana anterior.',
        ),
        t(k('week.2'), 'Pide a tu gestoría el PyG en un formato que entiendas, no solo el que exige Hacienda.'),
        t(
            k('week.3'),
            'Anota los hechos de cada semana, como una rotura, un cambio de proveedor, una promoción o un evento en la zona, para tener con qué contrastar el número cuando lo mires.',
        ),
        t(
            k('week.4'),
            'Antes de tu próxima reunión con la gestoría, escribe tú primero qué crees que ha pasado con los números. Compáralo después con lo que te digan.',
        ),
        t(
            k('week.5'),
            'Si dos semanas seguidas no sabrías explicar una variación de más de 2 puntos en tu margen, ese es el síntoma de que el hueco es demasiado grande.',
        ),
    ]

    const tableRows = [
        {
            gestoria: t(k('table.row.1.gestoria'), 'Cerrar IVA, nóminas y modelos'),
            you: t(k('table.row.1.you'), 'Por qué bajó el ticket medio el jueves'),
        },
        {
            gestoria: t(k('table.row.2.gestoria'), 'Preparar el PyG mensual'),
            you: t(k('table.row.2.you'), 'Que el proveedor nuevo sube el food cost'),
        },
        {
            gestoria: t(k('table.row.3.gestoria'), 'Avisar de una desviación'),
            you: t(k('table.row.3.you'), 'Si la desviación es un problema o algo puntual'),
        },
        {
            gestoria: t(k('table.row.4.gestoria'), 'Cumplir plazos fiscales'),
            you: t(k('table.row.4.you'), 'Si eso se repite o fue un mes suelto'),
        },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'La gestoría ve la cifra, no la escena. Por qué controlar los números del restaurante no se puede delegar del todo, ni siquiera en la mejor gestoría.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Hostelero revisando una libreta de números y albaranes en una mesa del restaurante tras el servicio',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                <span className={articleUi.em}>
                                    {t(
                                        k('lead'),
                                        'Solo el 39% de los restaurantes españoles revisa su rentabilidad cada semana, el 90% no hace presupuesto anual y 6 de cada 10 no llegan a los cinco años abiertos, y el hilo que conecta esos tres datos es el mismo: controlar los números del restaurante no se puede delegar del todo, ni siquiera en la mejor gestoría.',
                                    )}
                                </span>
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('intro.1'),
                                    '¿A que sabrías explicar, sin mirar el PyG, por qué el mes pasado se te movió el margen? Lo sabes porque lo viviste: se rompió la cámara y tiraste género, cambiaste de proveedor de pescado, montaste una promoción de miércoles que no tiró como esperabas.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(k('intro.2'), 'Tu gestoría no estaba ahí. Ve la cifra, no la escena.')}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                {t(
                                    k('intro.3'),
                                    'Y ese hueco, entre el número y lo que de verdad pasó en tu local esa semana, es el que se paga caro cuando nadie se encarga de cerrarlo.',
                                )}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='quien' className={articleUi.h2}>
                        {t(k('h2.quien'), '¿Quién tiene que controlar los números de un restaurante?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.quien.1'),
                                'Aquí no hay dos bandos. Hay dos trabajos distintos que a menudo se confunden en uno solo.',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.tableWrap}>
                        <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                            <caption className='sr-only'>
                                {t(k('table.caption'), 'Lo que hace tu gestoría frente a lo que solo sabes tú')}
                            </caption>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.col.gestoria'), 'Lo que hace tu gestoría')}</th>
                                    <th scope='col'>{t(k('table.col.you'), 'Lo que solo sabes tú')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableRows.map(row => (
                                    <tr key={row.gestoria}>
                                        <th scope='row'>{row.gestoria}</th>
                                        <td>{row.you}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.quien.2'),
                                'Una gestoría, la tuya, cualquiera, hace bien su trabajo cuando cuadra las cuentas y te avisa de que algo se ha movido. Lo que no puede hacer, por mucho que se esfuerce, es decirte por qué se ha movido. Esa parte solo la tienes tú, porque solo tú estuviste en el turno.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.quien.3Before'),
                                'Si además quieres saber cada cuánto conviene mirarlos, ya hablamos de la cadencia exacta en ',
                            )}
                            <LearnArticleLink slug='cada-cuanto-revisar-numeros-restaurante'>
                                {t(k('linkCadencia'), 'este artículo')}
                            </LearnArticleLink>
                            {t(k('p.quien.3After'), '.')}
                        </p>
                    </div>

                    <h2 id='detalle' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.detalle'), 'El 70% se siente estable, pero solo el 39% revisa cada semana')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.detalle.1Before'),
                                'El estudio Retos y Desafíos de la Restauración en España, elaborado por TheFork con la consultora Toluna sobre 615 profesionales del sector, deja un dato incómodo: ',
                            )}
                            <span className={articleUi.em}>
                                {t(
                                    k('p.detalle.1Em'),
                                    'el 70% de los hosteleros considera que su situación económica es estable',
                                )}
                            </span>
                            {t(k('p.detalle.1Mid'), ', pero solo ')}
                            <span className={articleUi.em}>
                                {t(k('p.detalle.1Em2'), 'el 39% revisa su rentabilidad cada semana')}
                            </span>
                            {t(k('p.detalle.1After'), '. Más de la mitad mira sus números una única vez al mes.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.detalle.2'),
                                'Es decir: la mayoría se siente segura sin haber comprobado el dato con la frecuencia que le permitiría saberlo de verdad. La sensación de solvencia y la solvencia real no siempre van de la mano. Y la única forma de que vayan de la mano es que tú mismo mires, con frecuencia, y contrastes lo que dice el número con lo que sabes que ha pasado en la calle.',
                            )}
                        </p>
                    </div>

                    <h2 id='incomoda' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.incomoda'), 'La parte incómoda: nadie lo va a hacer por ti')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.incomoda.1Before'), 'Asumir este control cuesta tiempo, y la mayoría no lo hace. ')}
                            <span className={articleUi.em}>
                                {t(k('p.incomoda.1Em'), 'El 90% de los restaurantes no elabora un presupuesto anual')}
                            </span>
                            {t(
                                k('p.incomoda.1After'),
                                ', según ha repetido el cocinero Ferran Adrià, que atribuye buena parte de los cierres del sector no a la cocina, sino a la falta de gestión financiera.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.incomoda.1bBefore'),
                                'Ese 90% no es el mismo que la estimación de campo —alrededor del 90% sin cuenta de resultados— recogida en el estudio SEGITTUR. Misma cifra, distinto significado; la creencia de llevar los números en la cabeza la desmontamos en ',
                            )}
                            <LearnArticleLink slug='como-saber-si-mi-restaurante-es-rentable'>
                                {t(k('p.incomoda.1bLink'), 'cómo saber si tu restaurante es rentable')}
                            </LearnArticleLink>
                            {t(k('p.incomoda.1bAfter'), '.')}
                        </p>
                    </div>

                    <blockquote className={articleUi.quote}>
                        {t(k('quote.adria'), '«El tema de la gestión es muy, muy importante».')}
                    </blockquote>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.incomoda.2Before'), 'Y no lo dice desde la teoría: ')}
                            <span className={articleUi.em}>
                                {t(
                                    k('p.incomoda.2Em'),
                                    '6 de cada 10 restaurantes que abren no llegan a los cinco años',
                                )}
                            </span>
                            {t(k('p.incomoda.2After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.incomoda.3'),
                                'Delegar la ejecución en una gestoría no es el problema. El problema es delegar también el entendimiento: no saber qué dice tu PyG, no tener presupuesto, y enterarte de que algo iba mal cuando ya es tarde para arreglarlo.',
                            )}
                        </p>
                    </div>

                    <h2 id='ganas' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.ganas'), '¿Qué ganas si empiezas a controlar tú los números?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.ganas.1'), 'No es solo evitar el palo. También hay parte buena.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(k('p.ganas.2Lead'), 'Entiendes tu negocio de verdad.')}
                            </span>{' '}
                            {t(
                                k('p.ganas.2'),
                                'Qué está funcionando, qué sobra, qué te aporta margen y qué solo ocupa sitio en la carta. Eso no te lo cuenta nadie desde fuera con la precisión de mirarlo tú cada semana.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.ganas.3Lead'),
                                    'Aprendes a fiarte de tus propios números, no solo de los de otro.',
                                )}
                            </span>{' '}
                            {t(
                                k('p.ganas.3'),
                                'Yo mismo encontré fallos en la contabilidad trabajando con José Andrés, con un equipazo detrás. Si eso pasa con un equipo de ese nivel, imagina lo que puede pasar cuando nadie mira dos veces.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.ganas.4Lead'),
                                    'Sabes cuándo entra el dinero y por qué, y cuándo sale y por qué.',
                                )}
                            </span>{' '}
                            {t(
                                k('p.ganas.4'),
                                'Ese seguimiento es lo que evita que lo que tanto cuesta ganar se evapore, simplemente porque diste por hecho que ese control no estaba a tu alcance.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.ganas.5Before'),
                                'Y no hace falta dedicarle una hora al día a algo que no te gusta. A veces bastan ',
                            )}
                            <strong>{t(k('p.ganas.5Strong'), '15 minutos a la semana')}</strong>
                            {t(k('p.ganas.5After'), ' para encontrar cientos de euros de mejora en tu negocio.')}
                        </p>
                    </div>

                    <h2 id='cuenta-vieja' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.cuentaVieja'), '183.000 negocios sin termómetro semanal')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cuentaVieja.1'),
                                'España tiene más de 300.000 establecimientos de hostelería, según el Anuario de Hostelería de España. Si el 61% no revisa su rentabilidad semanalmente, el reverso del 39% que sí lo hace, hablamos de estimación propia de unos 183.000 negocios operando sin el termómetro puesto la mayor parte del mes.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.cuentaVieja.2'),
                                'No hace falta que la correlación con el dato de Adrià sea causal para que el mensaje cale: cuantos menos ojos propios haya puestos en el negocio, menos margen de reacción cuando algo se tuerce.',
                            )}
                        </p>
                    </div>

                    <h2 id='ejemplo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.ejemplo'), 'Un ejemplo con números')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.ejemplo.1Before'), 'Un restaurante factura ')}
                            <strong>{t(k('p.ejemplo.1Strong'), '30.000€/mes')}</strong>
                            {t(k('p.ejemplo.1Mid'), ' con un margen neto del 9% (')}
                            <strong>{t(k('p.ejemplo.1Strong2'), '2.700€')}</strong>
                            {t(
                                k('p.ejemplo.1After'),
                                '). Un mes cambia de proveedor de carne sin comparar precios y el food cost sube 2 puntos: ',
                            )}
                            <strong>{t(k('p.ejemplo.1Strong3'), '600€ menos de margen ese mes')}</strong>
                            {t(k('p.ejemplo.1End'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.ejemplo.2Before'),
                                'Si nadie lo mira hasta la reunión trimestral con la gestoría, el problema lleva tres meses corriendo: ',
                            )}
                            <strong>{t(k('p.ejemplo.2Strong'), '1.800€ perdidos')}</strong>
                            {t(k('p.ejemplo.2After'), ' antes de que alguien lo señale.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.ejemplo.3Before'),
                                'Si el dueño revisa el food cost cada semana (10-15 minutos), detecta la desviación en la primera semana, compara precios y decide si vuelve al proveedor anterior o renegocia. Coste real: una semana de margen peor, unos ',
                            )}
                            <strong>{t(k('p.ejemplo.3Strong'), '150€')}</strong>
                            {t(k('p.ejemplo.3After'), ', en lugar de 1.800€ en un trimestre.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.ejemplo.4'),
                                'La diferencia entre los dos escenarios no es el dato, es quién lo mira y cuándo.',
                            )}
                        </p>
                    </div>

                    <h2 id='esta-semana' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.estaSemana'), 'Cinco acciones para retomar el control esta semana')}
                    </h2>
                    <ol className={articleUi.numListGap2xl}>
                        {weekItems.map(item => (
                            <li key={item} className={articleUi.numItem}>
                                {item}
                            </li>
                        ))}
                    </ol>

                    <h2 id='matices' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.matices'), 'Qué hay que tomar con pinzas en estos datos')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.1'),
                                'El dato del 39% que revisa semanalmente sale de una encuesta de percepción a 615 profesionales, no de un cruce con datos reales de facturación. Puede haber sesgo de sobreestimación, algo habitual cuando se pregunta directamente al hostelero por sus propios hábitos.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.2'),
                                'El "6 de cada 10 no llega a los cinco años" de Ferran Adrià es una cifra que repite en distintas intervenciones desde hace meses, pero no hemos localizado el estudio o la fuente estadística original que la sustenta. La tratamos como una cifra reiterada por una voz con criterio en el sector, no como un dato oficial contrastado.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.3'),
                                'Que tu gestoría lleve los números no es el problema. El problema es que sea la única que los entiende. Si la próxima vez que te llegue el PyG no sabrías explicar por qué ha cambiado algo, dínoslo y te decimos por dónde empezar.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('closing.signoff'),
                                'Si sale algo nuevo sobre esto, lo cuentas aquí antes que en ningún sitio.',
                            )}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿Te llega el PyG y no sabrías explicar por qué ha cambiado?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Ese hueco entre el número y lo que pasa en tu local es el que cerramos con Tipi. Dínoslo y te decimos por dónde empezar.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={articleUi.link}
                                >
                                    {t(k(source.labelKey), source.fallback)}
                                </a>
                            </li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
